/**
 * Admin Site Config Editor
 * Edit design tokens, manage layout sections, toggle features
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save,
  RotateCcw,
  Loader2,
  Settings,
  Palette,
  Layout,
  Flag,
  History,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  Type,
  Box,
  Sparkles,
  Check,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useAuth } from '../../App';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { API_URL } from '../../config/api';

// Color picker component
const ColorInput = ({ label, value, onChange }) => {
  return (
    <div className="flex items-center gap-3">
      <div 
        className="w-8 h-8 rounded-lg border border-zinc-600 cursor-pointer"
        style={{ backgroundColor: value }}
      >
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-full opacity-0 cursor-pointer"
        />
      </div>
      <div className="flex-1">
        <Label className="text-xs text-zinc-400">{label}</Label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 bg-zinc-800 border-zinc-700 text-sm font-mono"
          placeholder="#000000"
        />
      </div>
    </div>
  );
};

// Section editor row
const SectionRow = ({ 
  section, 
  index, 
  total,
  onToggle, 
  onMoveUp, 
  onMoveDown, 
  onEdit,
  onDelete,
  allowedTypes 
}) => {
  const typeColors = {
    Hero: 'bg-blue-500/20 border-blue-500/30',
    ComicRail: 'bg-orange-500/20 border-orange-500/30',
    ComicGrid: 'bg-green-500/20 border-green-500/30',
    CreatorRail: 'bg-purple-500/20 border-purple-500/30',
    CalloutBanner: 'bg-red-500/20 border-red-500/30',
    CategoryChips: 'bg-cyan-500/20 border-cyan-500/30',
  };

  return (
    <div 
      className={`p-3 rounded-lg border ${typeColors[section.type] || 'bg-zinc-800/50 border-zinc-700'} ${!section.enabled ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center gap-3">
        {/* Reorder buttons */}
        <div className="flex flex-col gap-0.5">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className={`p-1 rounded hover:bg-white/10 ${index === 0 ? 'opacity-30' : ''}`}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className={`p-1 rounded hover:bg-white/10 ${index === total - 1 ? 'opacity-30' : ''}`}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        {/* Section info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {section.type}
            </Badge>
            <span className="text-sm font-medium text-white truncate">
              {section.props?.title || section.id}
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">
            ID: {section.id}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={onEdit}
            className="h-8 px-2"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Switch
            checked={section.enabled}
            onCheckedChange={onToggle}
            className="data-[state=checked]:bg-green-500"
          />
          <button
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-red-500/20 text-zinc-400 hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminSiteConfig = () => {
  useDocumentTitle('Site Config - Admin');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [changeNote, setChangeNote] = useState('');

  // Edit states
  const [editingSection, setEditingSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin && user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  // Fetch config
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/site-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConfig(response.data);
      setRevisions(response.data.revisions || []);
      setAllowedTypes(response.data.allowed_section_types || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load site config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Update tokens
  const updateToken = (category, key, value) => {
    setConfig(prev => ({
      ...prev,
      tokens: {
        ...prev.tokens,
        [category]: {
          ...prev.tokens[category],
          [key]: value
        }
      }
    }));
    setHasChanges(true);
  };

  // Update feature flag
  const updateFeatureFlag = (key, value) => {
    setConfig(prev => ({
      ...prev,
      feature_flags: {
        ...prev.feature_flags,
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  // Update home sections
  const updateHomeSections = (sections) => {
    setConfig(prev => ({
      ...prev,
      layouts: {
        ...prev.layouts,
        home: {
          ...prev.layouts.home,
          sections
        }
      }
    }));
    setHasChanges(true);
  };

  // Section actions
  const toggleSection = (sectionId) => {
    const sections = config.layouts.home.sections.map(s =>
      s.id === sectionId ? { ...s, enabled: !s.enabled } : s
    );
    updateHomeSections(sections);
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    const sections = [...config.layouts.home.sections];
    [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
    updateHomeSections(sections);
  };

  const moveSectionDown = (index) => {
    const sections = config.layouts.home.sections;
    if (index >= sections.length - 1) return;
    const newSections = [...sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    updateHomeSections(newSections);
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    const sections = config.layouts.home.sections.filter(s => s.id !== sectionId);
    updateHomeSections(sections);
  };

  const updateSectionProps = (sectionId, props) => {
    const sections = config.layouts.home.sections.map(s =>
      s.id === sectionId ? { ...s, props: { ...s.props, ...props } } : s
    );
    updateHomeSections(sections);
  };

  const addSection = (type) => {
    const newSection = {
      id: `${type.toLowerCase()}_${Date.now().toString(36)}`,
      type,
      enabled: true,
      preset: `${type.toLowerCase().replace(/[A-Z]/g, m => '.' + m.toLowerCase())}.default`.replace('..', '.'),
      props: {
        title: `New ${type}`,
      }
    };
    const sections = [...config.layouts.home.sections, newSection];
    updateHomeSections(sections);
    setShowAddSection(false);
  };

  // Save config
  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/site-config`,
        {
          tokens: config.tokens,
          layouts: config.layouts,
          presets: config.presets,
          feature_flags: config.feature_flags,
          change_note: changeNote || 'Config update'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Site config saved!');
      setHasChanges(false);
      setChangeNote('');
      await fetchConfig();
    } catch (error) {
      console.error('Error saving config:', error);
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.validation_errors) {
        toast.error(`Validation errors: ${errorDetail.validation_errors.join(', ')}`);
      } else {
        toast.error('Failed to save config');
      }
    } finally {
      setSaving(false);
    }
  };

  // Rollback
  const rollback = async (version) => {
    if (!window.confirm(`Rollback to version ${version}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/site-config/rollback?version=${version}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Rolled back to version ${version}`);
      setShowHistory(false);
      await fetchConfig();
    } catch (error) {
      console.error('Error rolling back:', error);
      toast.error('Failed to rollback');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const colors = config?.tokens?.colors || {};
  const typography = config?.tokens?.typography || {};
  const spacing = config?.tokens?.spacing || {};
  const radius = config?.tokens?.radius || {};
  const featureFlags = config?.feature_flags || {};
  const homeSections = config?.layouts?.home?.sections || [];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-site-config">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-zinc-900 border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-400" />
              Site Config
            </h1>
            <Badge variant="outline" className="border-zinc-600">
              v{config?.version || 1}
            </Badge>
            {hasChanges && (
              <Badge className="bg-yellow-500/20 text-yellow-400">
                Unsaved Changes
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(true)}
              className="border-zinc-700"
              disabled={revisions.length === 0}
            >
              <History className="w-4 h-4 mr-1" />
              History
            </Button>
            
            <Button
              size="sm"
              onClick={saveConfig}
              disabled={saving || !hasChanges}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="tokens" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="tokens" className="data-[state=active]:bg-orange-500">
              <Palette className="w-4 h-4 mr-2" />
              Design Tokens
            </TabsTrigger>
            <TabsTrigger value="layout" className="data-[state=active]:bg-orange-500">
              <Layout className="w-4 h-4 mr-2" />
              Homepage Layout
            </TabsTrigger>
            <TabsTrigger value="flags" className="data-[state=active]:bg-orange-500">
              <Flag className="w-4 h-4 mr-2" />
              Feature Flags
            </TabsTrigger>
          </TabsList>

          {/* Design Tokens Tab */}
          <TabsContent value="tokens" className="space-y-6">
            {/* Colors */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-orange-400" />
                  Colors
                </CardTitle>
                <CardDescription>Brand colors and UI color tokens</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(colors).map(([key, value]) => (
                    <ColorInput
                      key={key}
                      label={key}
                      value={value}
                      onChange={(v) => updateToken('colors', key, v)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Typography */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type className="w-5 h-5 text-blue-400" />
                  Typography
                </CardTitle>
                <CardDescription>Font settings and text scales</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Heading Font</Label>
                    <Input
                      value={typography.fontFamilyHeading || 'Inter'}
                      onChange={(e) => updateToken('typography', 'fontFamilyHeading', e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Body Font</Label>
                    <Input
                      value={typography.fontFamilyBody || 'Inter'}
                      onChange={(e) => updateToken('typography', 'fontFamilyBody', e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-zinc-400">Base Font Size</Label>
                      <span className="text-sm text-zinc-500">{typography.baseFontSize || 16}px</span>
                    </div>
                    <Slider
                      value={[typography.baseFontSize || 16]}
                      onValueChange={([v]) => updateToken('typography', 'baseFontSize', v)}
                      min={12}
                      max={24}
                      step={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-zinc-400">Line Height</Label>
                      <span className="text-sm text-zinc-500">{typography.lineHeight || 1.5}</span>
                    </div>
                    <Slider
                      value={[(typography.lineHeight || 1.5) * 100]}
                      onValueChange={([v]) => updateToken('typography', 'lineHeight', v / 100)}
                      min={100}
                      max={200}
                      step={5}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Spacing & Radius */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Box className="w-5 h-5 text-green-400" />
                    Spacing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(spacing).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-3">
                        <span className="w-12 text-sm text-zinc-400">{key}</span>
                        <Slider
                          value={[value]}
                          onValueChange={([v]) => updateToken('spacing', key, v)}
                          min={0}
                          max={64}
                          step={2}
                          className="flex-1"
                        />
                        <span className="w-12 text-sm text-zinc-500 text-right">{value}px</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Box className="w-5 h-5 text-purple-400" />
                    Border Radius
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(radius).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-3">
                        <span className="w-12 text-sm text-zinc-400">{key}</span>
                        <Slider
                          value={[value]}
                          onValueChange={([v]) => updateToken('radius', key, v)}
                          min={0}
                          max={32}
                          step={2}
                          className="flex-1"
                        />
                        <span className="w-12 text-sm text-zinc-500 text-right">{value}px</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Homepage Layout Tab */}
          <TabsContent value="layout" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Homepage Sections</CardTitle>
                    <CardDescription>Drag to reorder, toggle visibility, edit props</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowAddSection(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Section
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {homeSections.map((section, index) => (
                    <SectionRow
                      key={section.id}
                      section={section}
                      index={index}
                      total={homeSections.length}
                      onToggle={() => toggleSection(section.id)}
                      onMoveUp={() => moveSectionUp(index)}
                      onMoveDown={() => moveSectionDown(index)}
                      onEdit={() => setEditingSection(section)}
                      onDelete={() => deleteSection(section.id)}
                      allowedTypes={allowedTypes}
                    />
                  ))}

                  {homeSections.length === 0 && (
                    <div className="text-center py-8 text-zinc-500">
                      <Layout className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No sections added yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Flags Tab */}
          <TabsContent value="flags" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="w-5 h-5 text-yellow-400" />
                  Feature Flags
                </CardTitle>
                <CardDescription>Toggle features on/off across the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(featureFlags).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50">
                      <div>
                        <p className="font-medium text-white">{key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-zinc-400 font-mono">{key}</p>
                      </div>
                      <Switch
                        checked={value}
                        onCheckedChange={(v) => updateFeatureFlag(key, v)}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Change Note */}
        {hasChanges && (
          <Card className="bg-zinc-900 border-zinc-800 mt-6">
            <CardContent className="pt-6">
              <Label className="text-zinc-400">Change Note (optional)</Label>
              <Input
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="Describe your changes..."
                className="bg-zinc-800 border-zinc-700 mt-2"
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Section Dialog */}
      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
            <DialogDescription>Choose a section type to add</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-4">
            {allowedTypes.map((type) => (
              <button
                key={type}
                onClick={() => addSection(type)}
                className="p-3 rounded-lg border border-zinc-700 text-left hover:border-orange-500/50 transition-colors"
              >
                <span className="font-medium text-sm">{type}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Section Dialog */}
      <Dialog open={!!editingSection} onOpenChange={() => setEditingSection(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Section: {editingSection?.type}</DialogTitle>
            <DialogDescription>Configure section properties</DialogDescription>
          </DialogHeader>
          {editingSection && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Title</Label>
                <Input
                  value={editingSection.props?.title || ''}
                  onChange={(e) => {
                    const updated = { ...editingSection, props: { ...editingSection.props, title: e.target.value } };
                    setEditingSection(updated);
                  }}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>

              {editingSection.props?.limit !== undefined && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-zinc-400">Limit</Label>
                    <span className="text-sm text-zinc-500">{editingSection.props?.limit || 12}</span>
                  </div>
                  <Slider
                    value={[editingSection.props?.limit || 12]}
                    onValueChange={([v]) => {
                      const updated = { ...editingSection, props: { ...editingSection.props, limit: v } };
                      setEditingSection(updated);
                    }}
                    min={4}
                    max={50}
                    step={1}
                  />
                </div>
              )}

              {editingSection.props?.columns && (
                <div className="space-y-3">
                  <Label className="text-zinc-400">Columns</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {['mobile', 'tablet', 'desktop'].map((device) => (
                      <div key={device} className="space-y-1">
                        <Label className="text-xs text-zinc-500 capitalize">{device}</Label>
                        <Input
                          type="number"
                          min={1}
                          max={device === 'desktop' ? 8 : device === 'tablet' ? 6 : 4}
                          value={editingSection.props?.columns?.[device] || 2}
                          onChange={(e) => {
                            const updated = { 
                              ...editingSection, 
                              props: { 
                                ...editingSection.props, 
                                columns: { 
                                  ...editingSection.props?.columns, 
                                  [device]: parseInt(e.target.value) 
                                } 
                              } 
                            };
                            setEditingSection(updated);
                          }}
                          className="bg-zinc-800 border-zinc-700 h-8"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editingSection.props?.showViewAll !== undefined && (
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400">Show View All Link</Label>
                  <Switch
                    checked={editingSection.props?.showViewAll}
                    onCheckedChange={(v) => {
                      const updated = { ...editingSection, props: { ...editingSection.props, showViewAll: v } };
                      setEditingSection(updated);
                    }}
                  />
                </div>
              )}

              {editingSection.props?.viewAllHref !== undefined && (
                <div className="space-y-2">
                  <Label className="text-zinc-400">View All Link</Label>
                  <Input
                    value={editingSection.props?.viewAllHref || ''}
                    onChange={(e) => {
                      const updated = { ...editingSection, props: { ...editingSection.props, viewAllHref: e.target.value } };
                      setEditingSection(updated);
                    }}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="/browse"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSection(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (editingSection) {
                  updateSectionProps(editingSection.id, editingSection.props);
                  setEditingSection(null);
                }
              }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Check className="w-4 h-4 mr-1" />
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>Rollback to a previous version</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4 max-h-80 overflow-y-auto">
            {revisions.map((rev) => (
              <div
                key={rev.version}
                className="flex items-center justify-between p-3 rounded-lg bg-zinc-800 border border-zinc-700"
              >
                <div>
                  <p className="font-medium">Version {rev.version}</p>
                  <p className="text-xs text-zinc-500">
                    {rev.updated_at ? new Date(rev.updated_at).toLocaleString() : 'Unknown'}
                  </p>
                  <p className="text-xs text-zinc-400">{rev.change_note}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => rollback(rev.version)}
                  className="border-zinc-600"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Restore
                </Button>
              </div>
            ))}

            {revisions.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No version history</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSiteConfig;
